// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportConfigModel.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var CONSTANTS = require('logic/data/Izenda.ExportConstants');
  var DEFAULT_VALUE = CONSTANTS.DEFAULT_VALUE;
  var DEFAULT_SETTINGS = CONSTANTS.DEFAULT_SETTINGS;
  var PrintConstants = require('logic/data/Izenda.PrintConstants');
  var MARGIN_KEY = CONSTANTS.MARGIN_KEY;
  const { getOr, uniqueId, map, forEach, filter, isEmpty, invokeArgs, find } = require('lodash/fp');

  function MarginSettings(data) {
    var props = {
      type: DEFAULT_VALUE,
      top: DEFAULT_VALUE,
      bottom: DEFAULT_VALUE,
      left: DEFAULT_VALUE,
      right: DEFAULT_VALUE,
      header: DEFAULT_VALUE,
      footer: DEFAULT_VALUE
    };
    m.extendObservable(trackable(this), props);
    data && this.readData(data);
  }

  /**
   * Read data for margin setting
   * @param  {object} data
   */
  MarginSettings.prototype.readData = m.action(function(data) {
    this.type = data.type;
    this.top = data.topValue;
    this.bottom = data.bottomValue;
    this.left = data.leftValue;
    this.right = data.rightValue;
    this.header = data.headerValue;
    this.footer = data.footerValue;
  });

  /**
   * Get data for savings
   * @return  {object} data
   */
  MarginSettings.prototype.prepairData = function() {
    return {
      type: this.type,
      topValue: this.top,
      bottomValue: this.bottom,
      leftValue: this.left,
      rightValue: this.right,
      headerValue: this.header,
      footerValue: this.footer
    };
  };

  /**
   * Get data for savings
   * @param  {float} value
   * @param  {string} key
   */
  MarginSettings.prototype.updateValueByKey = m.action(function(value, key) {
    this[key] = value;
    this.markDirty();
  });

  function ExportConfigModel(data) {
    var props = {
      id: uniqueId(),
      orientation: DEFAULT_VALUE,
      margins: DEFAULT_VALUE,
      centerOnPage: {
        horizontally: false,
        vertically: false
      },
      pageBreakAfterReportPart: false,
      pageSizeKey: PrintConstants.DEFAULT_PAGE_SIZE_KEY,
      scaleX: 1,
      scaleY: 1,
      marginSettings: this.initMarginSettings(DEFAULT_SETTINGS),
      getAllMarginSettings: function() {
        return map(function(item) {
          return item.prepairData();
        }, this.marginSettings);
      },
      getMarginSetting: function() {
        return (
          find(
            {
              type: this.margins
            },
            this.marginSettings
          ) || {}
        );
      },
      customMarginSettings: function() {
        return find(
          {
            type: CONSTANTS.MARGIN_VALUE.CUSTOM
          },
          this.marginSettings
        );
      },
      marginSettingInInches: function() {
        var margin = find(
          {
            type: this.margins
          },
          this.marginSettings
        );
        if (!margin)
          return {
            margin: 0
          };

        var rawMargin = m.toJS(margin);
        var unit = 'in';
        return {
          marginTop: rawMargin.top && rawMargin.top + unit,
          marginRight: rawMargin.right && rawMargin.right + unit,
          marginBottom: rawMargin.bottom && rawMargin.bottom + unit,
          marginLeft: rawMargin.left && rawMargin.left + unit,
          header: rawMargin.header && rawMargin.header + unit,
          footer: rawMargin.footer && rawMargin.footer + unit
        };
      }
    };

    m.extendObservable(trackable(this), props);
    data && this.readFromReport(data);
  }

  /**
   * Read data for export config
   * @param  {object} reportDefintions
   */
  ExportConfigModel.prototype.readFromReport = m.action(function(reportDefintions) {
    var exportConfig = reportDefintions.exportFormatSetting || {};
    var centerOnPage = exportConfig.centerOnPage || {};

    this.orientation = exportConfig.orientation;
    this.margins = exportConfig.margins;
    this.centerOnPage.horizontally = centerOnPage.horizontally;
    this.centerOnPage.vertically = centerOnPage.vertically;
    this.pageBreakAfterReportPart = exportConfig.pageBreakAfterReportPart;
    this.updateMarginSettings(exportConfig.marginSettings);
  });

  /**
   * Init margin config
   * @param  {array} data
   */
  ExportConfigModel.prototype.initMarginSettings = m.action(function(data) {
    return map(function(item) {
      return new MarginSettings(item);
    }, data);
  });

  /**
   * Update margin config
   * @param  {array} data
   */
  ExportConfigModel.prototype.updateMarginSettings = m.action(function(data) {
    forEach(
      function(item) {
        item &&
          invokeArgs(
            'readData',
            [item],
            find(
              {
                type: item.type
              },
              this.marginSettings
            )
          );
      }.bind(this),
      data
    );
  });

  /**
   * Get export config for saving
   * @return  {object} data
   */
  ExportConfigModel.prototype.getExportConfig = function() {
    return {
      orientation: this.orientation,
      margins: this.margins,
      centerOnPage: this.centerOnPage,
      pageBreakAfterReportPart: this.pageBreakAfterReportPart,
      marginSettings: this.getAllMarginSettings
    };
  };

  /**
   * Update export settings by key
   * @param  {string} value
   * @param  {string} key
   */
  ExportConfigModel.prototype.updateValueByKey = m.action(function(value, key) {
    this[key] = value;
    this.markDirty();
  });

  /**
   * Update center on page settings by key
   * @param  {bool} value
   * @param  {string} key
   */
  ExportConfigModel.prototype.updateCenterOnPageByKey = m.action(function(value, key) {
    this.centerOnPage[key] = value;
    this.markDirty();
  });

  /**
   * Check model dirty
   * @return {bool} result
   */
  ExportConfigModel.prototype.checkDirty = function() {
    var dirtyItems = filter(function(item) {
      return item.checkDirty && item.checkDirty();
    }, this.marginSettings);

    return !isEmpty(dirtyItems) || this.isDirty;
  };

  /**
   * clean model dirty
   */
  ExportConfigModel.prototype.cleanDirty = m.action(function() {
    this.resetDirty();
    forEach(function(marginSetting) {
      marginSetting.resetDirty();
    }, this.marginSettings);
  });

  /**
   * Get max value by key
   * @param {string} key
   * @returns {int} max value
   */
  ExportConfigModel.prototype.getMaxByKey = function(key) {
    var pageSize = PrintConstants.getPageSize(this.pageSizeKey, this.orientation);
    var marginSetings = this.getMarginSetting;
    var minWidth = CONSTANTS.MIN_WIDTH;
    var minHeight = CONSTANTS.MIN_HEIGHT;

    var rightValue = getOr(0, 'right', marginSetings);
    var leftValue = getOr(0, 'left', marginSetings);
    var bottomValue = getOr(0, 'bottom', marginSetings);
    var topValue = getOr(0, 'top', marginSetings);
    var headerValue = getOr(0, 'header', marginSetings);
    var footerValue = getOr(0, 'footer', marginSetings);

    var result = CONSTANTS.MAX_HEIGHT;

    switch (key) {
      case MARGIN_KEY.LEFT:
        result = pageSize.widthInInches - rightValue - minWidth;
        break;
      case MARGIN_KEY.RIGHT:
        result = pageSize.widthInInches - leftValue - minWidth;
        break;
      case MARGIN_KEY.TOP:
        result = pageSize.heightInInches - minHeight - (bottomValue + headerValue + footerValue);
        break;
      case MARGIN_KEY.BOTTOM:
        result = pageSize.heightInInches - minHeight - (topValue + headerValue + footerValue);
        break;
      case MARGIN_KEY.HEADER:
        result = pageSize.heightInInches - minHeight - (bottomValue + topValue + footerValue);
        break;
      case MARGIN_KEY.FOOTER:
        result = pageSize.heightInInches - minHeight - (bottomValue + headerValue + topValue);
        break;
    }

    return Math.round(result * 100) / 100;
  };

  module.exports = ExportConfigModel;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ExportConfigModel.js